import { apiRequest } from './http';
import type {
  ApiItemResponse,
  As2CertificateCreateRequest,
  As2CertificateHealthResponse,
  As2CertificateListQuery,
  As2CertificateRecord,
} from '../types/api';

function queryString(query: As2CertificateListQuery): string {
  const search = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });

  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

// The AS2 gateway paginates with Spring-style `{ content, totalElements }` envelopes
// (not this app's usual `{ data, total, page, pageSize }` shape), so the list call below
// normalizes it into the app's standard list-result shape for reuse with the existing
// list/pagination UI conventions.
interface GatewayPage<T> {
  content: T[];
  totalElements: number;
}

export interface As2CertificateListResult {
  data: As2CertificateRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export const as2CertificatesApi = {
  list: async (query: As2CertificateListQuery): Promise<As2CertificateListResult> => {
    const response = await apiRequest<ApiItemResponse<GatewayPage<As2CertificateRecord>>>(
      `/as2/certificates${queryString(query)}`,
    );
    return {
      data: response.data.content,
      total: response.data.totalElements,
      page: query.page ?? 0,
      pageSize: query.size ?? 20,
    };
  },
  health: async (): Promise<As2CertificateHealthResponse> =>
    (await apiRequest<ApiItemResponse<As2CertificateHealthResponse>>('/as2/certificates/health')).data,
  get: async (id: string): Promise<As2CertificateRecord> =>
    (await apiRequest<ApiItemResponse<As2CertificateRecord>>(`/as2/certificates/${id}`)).data,
  create: async (payload: As2CertificateCreateRequest): Promise<As2CertificateRecord> =>
    (await apiRequest<ApiItemResponse<As2CertificateRecord>>('/as2/certificates', {
      method: 'POST',
      body: JSON.stringify(payload),
    })).data,
  deactivate: async (id: string): Promise<As2CertificateRecord> =>
    (await apiRequest<ApiItemResponse<As2CertificateRecord>>(`/as2/certificates/${id}/deactivate`, {
      method: 'POST',
    })).data,
};
